import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import * as telemarketingService from '../../services/crm/telemarketingService';
import { toast } from 'react-hot-toast';

export default function TelemarketingCampaignDetail() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { activeOrgId } = useCrmOrgStore();
  const [campaign, setCampaign] = useState<any>(null);
  const [stats, setStats] = useState<telemarketingService.CampaignStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null); // Track which action is loading
  const [contactsText, setContactsText] = useState('');
  const [addingContacts, setAddingContacts] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importingCsv, setImportingCsv] = useState(false);
  const [targets, setTargets] = useState<any[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [removingTargetId, setRemovingTargetId] = useState<string | null>(null);

  const loadTargets = useCallback(async () => {
    if (!campaignId || !activeOrgId) return;
    try {
      setLoadingTargets(true);
      const result = await telemarketingService.getCampaignTargets(campaignId, activeOrgId);
      setTargets(result.data || []);
    } catch (error) {
      console.error('Failed to load targets:', error);
    } finally {
      setLoadingTargets(false);
    }
  }, [campaignId, activeOrgId]);

  const loadCampaign = useCallback(async () => {
    if (!campaignId || !activeOrgId) return;
    try {
      setLoading(true);
      const [campaignData, statsData] = await Promise.all([
        telemarketingService.getCampaign(campaignId, activeOrgId) as Promise<any>,
        telemarketingService.getCampaignStats(campaignId).catch(() => null)
      ]);
      setCampaign(campaignData);
      setStats(statsData);
    } catch (error: any) {
      console.error('Failed to load campaign:', error);
      toast.error('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  }, [campaignId, activeOrgId]);

  useEffect(() => {
    if (campaignId && activeOrgId) {
      loadCampaign();
      loadTargets();
    }
  }, [campaignId, activeOrgId, loadCampaign, loadTargets]);

  const handleStart = async () => {
    if (!campaign || actionLoading) return;
    try {
      setActionLoading('start');
      await telemarketingService.startCampaign(campaign.id, activeOrgId!);
      setCampaign((prev: any) => prev ? { ...prev, status: 'active' } : prev);
      toast.success('Campaign started!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to start campaign');
    } finally {
      setActionLoading(null);
    }
  };

  const handleImportCsv = async () => {
    if (!csvFile || !campaignId || !activeOrgId) return;
    try {
      setImportingCsv(true);
      const csvContent = await csvFile.text();
      const result = await telemarketingService.importCSVToCampaign(campaignId, activeOrgId, csvContent, true);
      const imported = result?.validContacts ?? 0;
      toast.success(`Imported ${imported} contacts from CSV`);
      setCsvFile(null);
      await loadCampaign();
      await loadTargets();
    } catch (error: any) {
      console.error('CSV import failed:', error);
      toast.error(error?.message || 'Failed to import CSV');
    } finally {
      setImportingCsv(false);
    }
  };

  const handlePause = async () => {
    if (!campaign || actionLoading) return;
    try {
      setActionLoading('pause');
      await telemarketingService.pauseCampaign(campaign.id, activeOrgId!);
      setCampaign((prev: any) => prev ? { ...prev, status: 'paused' } : prev);
      toast.success('Campaign paused');
    } catch (error: any) {
      toast.error(error.message || 'Failed to pause campaign');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!campaign || actionLoading) return;
    
    try {
      setActionLoading('delete');
      await telemarketingService.deleteCampaign(campaign.id, activeOrgId!);
      toast.success('Campaign deleted');
      navigate('/crm/telemarketing');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete campaign');
      setActionLoading(null);
      setShowDeleteConfirm(false);
    }
  };

  const handleRemoveTarget = async (targetId: string) => {
    if (removingTargetId) return;
    try {
      setRemovingTargetId(targetId);
      await telemarketingService.removeTarget(targetId);
      setTargets(prev => prev.filter(t => t.id !== targetId));
      toast.success('Contact removed');
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove contact');
    } finally {
      setRemovingTargetId(null);
    }
  };

  const handleAddContacts = async () => {
    if (!contactsText.trim() || !campaignId || !activeOrgId) return;
    
    try {
      setAddingContacts(true);
      
      // Parse contacts from text (format: name, phone per line)
      const lines = contactsText.trim().split('\n').filter(l => l.trim());
      const contacts = lines.map(line => {
        const parts = line.split(',').map(p => p.trim());
        return {
          name: parts[0] || 'Unknown',
          phone: parts[1] || parts[0], // If only one value, assume it's the phone
          company: parts[2] || undefined
        };
      }).filter(c => c.phone);
      
      if (contacts.length === 0) {
        toast.error('No valid contacts found. Use format: Name, Phone per line');
        return;
      }
      
      await telemarketingService.addCampaignTargets(campaignId, activeOrgId, contacts);
      toast.success(`Added ${contacts.length} contacts!`);
      setContactsText('');
      loadCampaign(); // Refresh stats
      loadTargets(); // Refresh contacts list
    } catch (error: any) {
      console.error('Failed to add contacts:', error);
      toast.error(error.message || 'Failed to add contacts');
    } finally {
      setAddingContacts(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <p className="text-gray-600">Campaign not found</p>
        <Link to="/crm/telemarketing" className="text-purple-600 hover:underline">← Back to campaigns</Link>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700',
    active: 'bg-green-100 text-green-700',
    paused: 'bg-yellow-100 text-yellow-700',
    completed: 'bg-blue-100 text-blue-700',
    archived: 'bg-gray-200 text-gray-600'
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/crm/telemarketing" className="text-purple-600 hover:underline text-sm mb-2 inline-block">
            ← Back to campaigns
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">{campaign.name}</h1>
          <div className="flex items-center gap-3 mt-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[campaign.status] || 'bg-gray-100'}`}>
              {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
            </span>
            {campaign.description && (
              <span className="text-gray-600">{campaign.description}</span>
            )}
          </div>
        </div>
        
        <div className="flex gap-3">
          {campaign.status === 'draft' || campaign.status === 'paused' ? (
            <button
              onClick={handleStart}
              disabled={!!actionLoading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {actionLoading === 'start' ? 'Starting...' : '▶ Start Campaign'}
            </button>
          ) : campaign.status === 'active' ? (
            <button
              onClick={handlePause}
              disabled={!!actionLoading}
              className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
            >
              {actionLoading === 'pause' ? 'Pausing...' : '⏸ Pause'}
            </button>
          ) : null}
          
          <Link
            to={`/crm/telemarketing/${campaign.id}/calls`}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            📞 Call Monitor
          </Link>
          
          <Link
            to={`/crm/telemarketing/${campaign.id}/analytics`}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            📊 Analytics
          </Link>
          
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={!!actionLoading}
            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50"
            title="Delete campaign"
          >
            🗑️
          </button>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Campaign?</h3>
            <p className="text-gray-600 mb-4">
              This will permanently delete <strong>{campaign.name}</strong> and all associated contacts and call records. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={!!actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading === 'delete' ? 'Deleting...' : 'Delete Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Contacts</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_targets || 0}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Calls Made</p>
            <p className="text-2xl font-bold text-gray-900">{stats.completed_calls || 0}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Interested</p>
            <p className="text-2xl font-bold text-green-600">{stats.interested_count || 0}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Meetings Booked</p>
            <p className="text-2xl font-bold text-purple-600">{stats.meetings_booked || 0}</p>
          </div>
        </div>
      )}

      {/* Add Contacts Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">➕ Add Contacts</h2>
        <p className="text-sm text-gray-600 mb-3">
          Enter contacts (one per line). Format: <code className="bg-gray-100 px-1">Name, Phone, Company</code>
        </p>
        <textarea
          value={contactsText}
          onChange={(e) => setContactsText(e.target.value)}
          placeholder="John Smith, +447911123456, Acme Corp&#10;Jane Doe, +447922234567, XYZ Ltd"
          className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm"
        />
        <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-gray-500">
            {contactsText.trim().split('\n').filter(l => l.trim()).length} contacts to add
          </span>
          <button
            onClick={handleAddContacts}
            disabled={addingContacts || !contactsText.trim()}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {addingContacts ? 'Adding...' : 'Add Contacts'}
          </button>
        </div>

        <div className="mt-6 border-t border-gray-200 pt-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Import from CSV</h3>
          <p className="text-xs text-gray-600 mb-3">
            CSV columns: <code className="bg-gray-100 px-1">first_name, last_name, phone</code> (optional: <code className="bg-gray-100 px-1">email, company, job_title, industry, notes</code>)
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <input
              type="file"
              accept=".csv"
              disabled={importingCsv}
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              className="block w-full text-sm"
            />
            <button
              onClick={handleImportCsv}
              disabled={importingCsv || !csvFile}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {importingCsv ? 'Importing...' : 'Import CSV'}
            </button>
          </div>
        </div>
      </div>

      {/* Contacts List */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">📋 Contacts ({targets.length})</h2>
        {loadingTargets ? (
          <div className="text-center py-4">Loading contacts...</div>
        ) : targets.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No contacts yet. Add contacts above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-3 font-medium">Name</th>
                  <th className="text-left py-3 px-3 font-medium">Phone</th>
                  <th className="text-left py-3 px-3 font-medium hidden md:table-cell">Company</th>
                  <th className="text-left py-3 px-3 font-medium">Status</th>
                  <th className="text-left py-3 px-3 font-medium hidden sm:table-cell">Attempts</th>
                  <th className="text-right py-3 px-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {targets.map((target: any) => (
                  <tr key={target.id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-3">{target.contact_name || '-'}</td>
                    <td className="py-3 px-3 font-mono text-sm">{target.phone_number}</td>
                    <td className="py-3 px-3 hidden md:table-cell">{target.company_name || '-'}</td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        target.call_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        target.call_status === 'completed' ? 'bg-green-100 text-green-700' :
                        target.call_status === 'calling' ? 'bg-blue-100 text-blue-700' :
                        target.call_status === 'failed' ? 'bg-red-100 text-red-700' :
                        target.call_status === 'do_not_call' ? 'bg-red-200 text-red-800' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {target.call_status === 'do_not_call' ? 'DNC' : target.call_status}
                      </span>
                    </td>
                    <td className="py-3 px-3 hidden sm:table-cell">{target.attempts || 0}</td>
                    <td className="py-3 px-3 text-right">
                      <button
                        onClick={() => handleRemoveTarget(target.id)}
                        disabled={removingTargetId === target.id}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50"
                        title="Remove from campaign"
                      >
                        {removingTargetId === target.id ? '...' : '✕ Remove'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Campaign Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Campaign Settings</h2>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600">Objective</p>
              <p className="font-medium">{campaign.objective || 'Not set'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Max Attempts</p>
              <p className="font-medium">{campaign.max_attempts || 3}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Retry Delay</p>
              <p className="font-medium">{campaign.retry_delay_hours || 24} hours</p>
            </div>
            {campaign.ai_agent_name && (
              <div>
                <p className="text-sm text-gray-600">AI Agent Name</p>
                <p className="font-medium">{campaign.ai_agent_name}</p>
              </div>
            )}
            {campaign.company_name && (
              <div>
                <p className="text-sm text-gray-600">Company</p>
                <p className="font-medium">{campaign.company_name}</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Schedule</h2>
          {campaign.call_schedule ? (
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Days</p>
                <p className="font-medium">{campaign.call_schedule.days?.join(', ') || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Hours</p>
                <p className="font-medium">
                  {campaign.call_schedule.start_time || '09:00'} - {campaign.call_schedule.end_time || '17:00'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Timezone</p>
                <p className="font-medium">{campaign.call_schedule.timezone || 'UTC'}</p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No schedule configured</p>
          )}
        </div>
      </div>

      {/* Script Template */}
      {campaign.script_template && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">Script Template</h2>
          <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-lg">
            {campaign.script_template}
          </pre>
        </div>
      )}

      {/* Value Proposition */}
      {campaign.value_proposition && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">Value Proposition</h2>
          <p className="text-gray-700">{campaign.value_proposition}</p>
        </div>
      )}

    </div>
  );
}
